import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError, fileUrl } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Star, FileText, MapPin } from "@phosphor-icons/react";
import { PassList, isPass } from "../components/PassList";

export default function Profile() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = () => api.get(`/profile/${id}`).then((r) => setP(r.data));
  useEffect(() => { load(); }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { job_id: "", to_user_id: id, rating: parseInt(rating), comment });
      setComment(""); toast.success(t("leave_review")); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (!p) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-zinc-500">{t("loading")}</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 py-8">
      <div className="bg-white border border-zinc-200 rounded-md p-6 flex items-center gap-5">
        <div className="w-20 h-20 rounded-full bg-zinc-100 overflow-hidden border border-zinc-200 shrink-0">
          {p.avatar_path ? <img src={fileUrl(p.avatar_path)} alt="avatar" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-head font-black text-2xl text-zinc-400">{p.name?.[0]}</div>}
        </div>
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{p.name}</h1>
          <div className="text-sm text-zinc-500 flex items-center gap-3 mt-1">
            <span className="capitalize">{p.role === "company" ? t("role_company") : t("role_guard")}</span>
            {p.city && <span className="flex items-center gap-1"><MapPin size={14} />{p.city}</span>}
          </div>
          {p.rating > 0 && <div className="flex items-center gap-1 text-sm mt-1"><Star size={15} weight="fill" className="text-[#FFCC00]" />{p.rating} · {p.rating_count} {t("reviews")}</div>}
        </div>
      </div>

      {p.bio && <div className="bg-white border border-zinc-200 rounded-md p-6 mt-4"><div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2">{t("bio")}</div><p className="text-zinc-700 whitespace-pre-line">{p.bio}</p></div>}

      {p.role === "guard" && p.documents?.some(isPass) && (
        <div className="bg-white border border-zinc-200 rounded-md p-6 mt-4">
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">{t("section_passes")}</div>
          <PassList documents={p.documents} />
        </div>
      )}

      {p.documents?.some((d) => !isPass(d)) && (
        <div className="bg-white border border-zinc-200 rounded-md p-6 mt-4">
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">{t("documents")}</div>
          <div className="space-y-2">{p.documents.filter((d) => !isPass(d)).map((d) => (
            <a key={d.path} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-zinc-700 hover:text-[#E11D2A]"><FileText size={16} />{d.name}</a>
          ))}</div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md p-6 mt-4">
        <div className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-3">{t("reviews")}</div>
        <div className="space-y-4">
          {p.reviews?.length === 0 && <p className="text-sm text-zinc-400">—</p>}
          {p.reviews?.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-zinc-100 pb-3 last:border-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{r.from_name}</span>
                <span className="flex">{[1,2,3,4,5].map((s) => <Star key={s} size={13} weight="fill" className={s <= r.rating ? "text-[#FFCC00]" : "text-zinc-200"} />)}</span>
              </div>
              {r.comment && <p className="text-sm text-zinc-600 mt-1">{r.comment}</p>}
            </div>
          ))}
        </div>

        {user && user.id !== id && (
          <form onSubmit={submitReview} className="mt-5 border-t border-zinc-100 pt-4">
            <div className="text-sm font-semibold mb-2">{t("leave_review")}</div>
            <div className="flex gap-1 mb-3">
              {[1,2,3,4,5].map((s) => (
                <button key={s} type="button" data-testid={`star-${s}`} onClick={() => setRating(s)}><Star size={24} weight="fill" className={s <= rating ? "text-[#FFCC00]" : "text-zinc-200"} /></button>
              ))}
            </div>
            <textarea data-testid="review-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("comment")} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D2A]" />
            <button data-testid="submit-review" className="mt-2 bg-[#E11D2A] text-white text-sm font-semibold rounded-md px-5 py-2 hover:bg-[#B0121F] transition-colors">{t("submit")}</button>
          </form>
        )}
      </div>
    </div>
  );
}
